// MST
import { types, flow, cast, IAnyModelType, applySnapshot, castToSnapshot, detach } from 'mobx-state-tree';

// Utils
import { withEnvironment } from 'models/extensions/with-environment';

// Interfaces
import {
  IAdditionalQuestionApiResponse,
  IAdditionalQuestionData,
  IGetAssesseeAdditionalQuestionAttrs,
  IGetPICAreaAdditionalQuestionAttrs,
  IUpdateAssesseeAdditionalQuestionAttrs,
} from 'interfaces/back-office/personal-data/additional-question';
// Interfaces
import { COLUMN_TYPE_ADDITIONAL_QUESTION } from 'constant/shared.constant';

// Types
const { model, maybeNull, optional, string, number, boolean, array, late, enumeration } = types;

// Config Answer Props
const configAnswerSubDetailModel = model('ConfigAnswerSubDetail').props({
  transactionAssessmentAnswerId: maybeNull(number),
  answerDescription: maybeNull(string),
});

// Config Answer Detail Props
const configAnswerDetailModel = model('ConfigAnswerDetailProps').props({
  answerDetailsId: maybeNull(number),
  answerDetailsCode: maybeNull(string),
  answerDetailsName: maybeNull(string),
  answerDetailsImage: maybeNull(string),
  answerDetailsScore: maybeNull(string),
  userAnswer: maybeNull(configAnswerSubDetailModel),
});

const AnswerModelModel = model('AnswerModelModel').props({
  questionId: maybeNull(number),
  answerId: maybeNull(number),
  atypeId: maybeNull(number),
  alayoutId: maybeNull(number),
  answerCode: maybeNull(string),
  answerName: maybeNull(string),
  answerDetails: optional(array(configAnswerDetailModel), []),
  userAnswerFile: maybeNull(string),
  userAnswerText: maybeNull(string)
})

const AnswerModel = model('AnswerModel').props({
  answerModel: optional(array(AnswerModelModel), [])
})
  

const QuestionModel = model('QuestionModel').props({
  questionId: maybeNull(number),
  questionCode: maybeNull(string),
  isAddQuestion: optional(boolean, false),
  isBlankQuestion: maybeNull(string),
  qtypeId: maybeNull(number),
  questionDesc: maybeNull(string),
  questionImage: maybeNull(number)
});

const HeaderModel = model('HeaderModel').props({
  headerName: maybeNull(string),
  headerId: maybeNull(number),
  parentHeaderId: maybeNull(number),
  cellCode: maybeNull(string),
  type: maybeNull(enumeration('Type', Object.values(COLUMN_TYPE_ADDITIONAL_QUESTION))),
  child: optional(array(late((): IAnyModelType => HeaderModel)), []),
});

const ResultModel = model('ResultModel').props({
  questions: maybeNull(QuestionModel),
  answers: maybeNull(AnswerModel),
  headerId: maybeNull(number),
  parentHeaderId: maybeNull(number)
});

const BodyModel = model('BodyModel').props({
  rowId: maybeNull(number),
  result: optional(array(ResultModel), []),
});

const PersonalQuestionModel = model('PersonalQuestionModel').props({
  headers: optional(array(HeaderModel), []),
  instructionDesc: maybeNull(string),
  qGroupDesc: maybeNull(string),
  qGroupId: maybeNull(number),
  qGroupName: maybeNull(string),
  body: optional(array(BodyModel), []),
});

const AdditionalQuestionData = model('IAdditionalQuestionData').props({
  personalQuestions: optional(array(PersonalQuestionModel), []),
});

// Base Model
const AdditionalQuestionModel = model('AdditionalQuestion')
  .props({
    additionalQuestion_isLoading: optional(boolean, false),
    additionalQuestion_isSaveLoading: optional(boolean, false),
    additionalQuestion_additionalQuestion: optional(AdditionalQuestionData, {}),
  })
  .extend(withEnvironment)
  .actions(self => ({
    additionalQuestion_SET_LOADING: (payload: boolean): void => {
      self.additionalQuestion_isLoading = payload;
    },
    additionalQuestion_SET_SAVE_LOADING: (payload: boolean): void => {
      self.additionalQuestion_isSaveLoading = payload;
    },
    additionalQuestion_SET_ADDITIONAL_QUESTION: (payload: IAdditionalQuestionData): void => {
      self.additionalQuestion_additionalQuestion = castToSnapshot(payload);
    },
  }))
  .actions(self => ({
    /**
     *  @description Fetch Additional Question as Assessee
     *
     *  @payload {IGetAssesseeAdditionalQuestionAttrs} payload
     *
     *  @return {void} void
     */
    additionalQuestion_fetchAdditionalQuestionAsAssessee: flow(function* (payload: IGetAssesseeAdditionalQuestionAttrs) {
      self.additionalQuestion_SET_LOADING(true);

      const response: IAdditionalQuestionApiResponse =
        yield self.environment.additionalQuestionApi.additionalQuestion_getAssesseeAdditionalQuestion({
          requestConfig: { ...payload.requestConfig },
        });

      if (response.ok) {
        self.additionalQuestion_SET_ADDITIONAL_QUESTION(response.res.data);

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError();
      }

      self.additionalQuestion_SET_LOADING(false);
    }),

    /**
     *  @description Fetch Additional Question as PIC Area
     *
     *  @payload {IGetPICAreaAdditionalQuestionAttrs} payload
     *
     *  @return {void} void
     */
    additionalQuestion_fetchAdditionalQuestionAsPICArea: flow(function* (payload: IGetPICAreaAdditionalQuestionAttrs) {
      self.additionalQuestion_SET_LOADING(true);

      const response: IAdditionalQuestionApiResponse =
        yield self.environment.additionalQuestionApi.additionalQuestion_getPICAreaAdditionalQuestion({
          requestConfig: { ...payload.requestConfig },
          params: { ...payload.params },
        });

      if (response.ok) {
        self.additionalQuestion_SET_ADDITIONAL_QUESTION(response.res.data);

        // Do when response success
        if (payload?.onSuccess) payload.onSuccess();
      }

      if (!response.ok) {
        // Do when response failed
        if (payload?.onError) payload.onError();
      }

      self.additionalQuestion_SET_LOADING(false);
    }),

    /**
     *  @description Update Additional Question as PIC Area
     *
     *  @payload {IGetPICAreaAdditionalQuestionAttrs} payload
     *
     *  @return {void} void
     */
    additionalQuestion_updateAdditionalQuestion: flow(function* (payload: IUpdateAssesseeAdditionalQuestionAttrs) {
      self.additionalQuestion_SET_SAVE_LOADING(true)

      const response = yield self.environment.additionalQuestionApi.additionalQuestion_updateAssesseeAdditionalQuestion({
        body: payload.body,
        requestConfig: { ...payload.requestConfig },
      })

      if(response.ok) {
        if(payload?.onSuccess) payload.onSuccess()
      }

      if(!response.ok) {
        if(payload?.onError) payload.onError()
      }

      self.additionalQuestion_SET_SAVE_LOADING(false)

    })
  }));

export const createAdditionalQuestionStoreDefaultModel = () => optional(AdditionalQuestionModel, {});
